import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileCode, Plus } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { useReportBundleStore } from '@/frontend/store/ReportBundleStore';
import { LoadingSpinner } from '../LoadingSpinner';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { CreateReportBundleModal } from './CreateReportBundleModal';
import { SchemaList } from './schemas/SchemaList';
import { EditedBy, PublishedCell, StatusDot } from './studioChrome';

/** Every report bundle — whether its draft compiles, the version published, who last edited it — and every schema they read. */
export function ReportStudioListPage() {
  const bundles = useReportBundleStore((s) => s.bundles);
  const fetchBundles = useReportBundleStore((s) => s.fetchBundles);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (bundles === null) void fetchBundles();
  }, [bundles, fetchBundles]);

  return (
    <div data-slot="report-studio" className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-8 text-sm text-foreground">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold"><FileCode className="size-5 text-muted-foreground" /> {I18n.get('reportStudio')}</h1>
          <p className="max-w-2xl text-muted-foreground">{I18n.get('reportStudioHelp')}</p>
        </div>
        <Button className="shrink-0" onClick={() => setShowCreate(true)}><Plus /> {I18n.get('newReport')}</Button>
      </div>

      <section className="flex flex-col gap-3" aria-labelledby="reports-heading">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="reports-heading" className="text-base font-semibold">{I18n.get('reports')}</h2>
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><StatusDot tone="ok" className="size-2" /> {I18n.get('reportBundleLegendCompiles')}</span>
            <span className="flex items-center gap-1.5"><StatusDot tone="bad" className="size-2" /> {I18n.get('reportBundleLegendBroken')}</span>
          </div>
        </div>
        <div className="overflow-x-auto rounded-xl border-[1px] bg-background">
          {bundles === null ? (
            <div className="flex justify-center py-10" aria-label={I18n.get('loading')}><LoadingSpinner /></div>
          ) : bundles.length === 0 ? (
            <p className="px-4 py-10 text-center text-muted-foreground">{I18n.get('reportBundleNoneYet')}</p>
          ) : (
            <Table>
              <TableHeader className="bg-muted/40">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-1/2 pl-4">{I18n.get('reportBundleTitle')}</TableHead>
                  <TableHead>{I18n.get('reportBundlePublishedColumn')}</TableHead>
                  <TableHead>{I18n.get('lastEdited')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bundles.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="pl-4 whitespace-normal">
                      <div className="flex items-start gap-2.5">
                        <StatusDot tone={b.draftCompiles ? 'ok' : 'bad'} className="mt-1.5 size-2" />
                        <span className="sr-only">{b.draftCompiles ? I18n.get('reportBundleLegendCompiles') : I18n.get('reportBundleLegendBroken')}</span>
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <Link to={`/report-studio/${b.id}`} className="font-medium text-foreground [overflow-wrap:anywhere] hover:text-primary hover:underline">{b.title}</Link>
                          {!b.draftCompiles && <span className="text-xs text-destructive">{I18n.get('reportBundleDraftBroken')}</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <PublishedCell
                        version={b.latestVersion ? { number: b.latestVersion.number, createdAt: b.latestVersion.createdAt } : null}
                        changed={b.draftChanged}
                      />
                    </TableCell>
                    <TableCell><EditedBy at={b.draftUpdatedAt} by={b.draftUpdatedBy} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>
      <SchemaList />
      {showCreate && <CreateReportBundleModal onHide={() => setShowCreate(false)} />}
    </div>
  );
}
